import { PageLayout } from '../../../components/layout';
import { AudioPlayerView, ChapterItem, TabItems } from '../components';
import { useContext, useEffect, useRef, useState } from 'react';
import { Text, useAudioPlayer } from '../../../components/common';
import { css } from '@emotion/css';
import { PodcastHeader } from '../components/header';
import { recordActivity } from '@repo-sapiens/helpers';
import { PodcastResponse } from '../../../types';
import { useLazyQuery } from '@apollo/client';
import { PODCAST } from '@repo-sapiens/graphql';
import { UserContext } from '../../../context';
import { useDemoMode, usePageViewed } from '../../../hooks';
import { fakePodcast } from '../../../demo-data';
import { differenceInSeconds, format } from 'date-fns';
import { EActivityEvent } from '@repo-sapiens/data-access-models';
import { isEqual } from 'lodash';
import { config } from '../../../config';
import { trackEvent } from '../../../libs';

const staticPodcasts = [
  {
    id: 'welcome',
    name: 'Welcome to Multiplayer',
    src: 'https://storage.googleapis.com/multiplayer-images-public/podcasts/welcome-pod.wav',
    transcript: `
Hey there and welcome to Multiplayer. I’m your AI executive assistant, here to help you focus on what matters and keep everything else moving in the background.<br><br>

This podcast is how I’ll check in with you each morning. Just a quick snapshot of your day: what’s coming up, what matters, and what you can safely ignore… because I’ve already taken care of it.<br><br>

You’ll get a quick rundown of your schedule, who you’re meeting, important emails, and any follow-ups that need your attention.<br><br>

I’ll also include anything else that seems worth your time … like something useful in a newsletter you already get, or an event nearby that’s worth checking out.<br><br>

And as I learn more about how you work and what you care about, these briefings will get clearer, more focused, and tailored to help you start your day strong.<br><br>

That’s it for now. Your first episode will land here tomorrow morning. I’ll talk to you then.<br><br>

`,
    duration: 49,
  },
];

export const PodcastScreen = () => {
  const [tab, setTab] = useState<'CHAPTERS' | 'TRANSCRIPT'>('CHAPTERS');
  const [chapter, setChapter] = useState<
    {
      id: string;
      name: string;
      src: string;
      transcript: string;
      duration: number;
    }[]
  >([]);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const { state: userState } = useContext(UserContext);
  const { demoMode } = useDemoMode();
  const startTimeRef = useRef(Date.now());

  usePageViewed(EActivityEvent.PodcastPageViewed);

  const [loadPodast, { loading }] = useLazyQuery<PodcastResponse>(PODCAST);

  const {
    addTrack,
    play,
    pause,
    currentTime,
    seekToTrackIndex,
    seekToTime,
    currentTrackIndex,
    isPlaying,
  } = useAudioPlayer();

  useEffect(() => {
    return () => {
      pause();
    };
  }, [pause]);

  useEffect(() => {
    const demoPodcast = demoMode
      ? fakePodcast.map((item) => ({
          id: item.chapter || '',
          name: item.chapter || '',
          src: item.url || '',
          transcript: item.transcript || '',
          duration: item.audioDuration || 0,
        }))
      : [];
    setChapter(demoPodcast);

    for (const item of demoPodcast) {
      if (item.src) {
        addTrack(item.src);
      }
    }
    const dayOffset = 0;
    loadPodast({
      fetchPolicy: 'network-only',
      //pollInterval: demoMode ? undefined : 60000, // 1 minute
      variables: {
        dayOffset,
      },
    })
      .then((result) => {
        if (result.data?.podcasts && !demoMode) {
          const date = new Date(result.data.podcasts[0].created);
          setLastUpdated(format(date, 'MMMM do • h:mm a'));
          const podcastChapters = result.data.podcasts
            .map((item) => ({
              id: item?._id || '',
              name: item?.title || '',
              src: item?.url || '',
              transcript: item?.transcript || '',
              duration: item?.audioDuration || 90,
            }))
            .filter((item) => item?.src);
          setChapter((prev) => {
            if (isEqual(prev, podcastChapters)) {
              return prev;
            }
            return podcastChapters;
          });
          for (const item of result.data.podcasts) {
            if (item?.url) {
              addTrack(item.url);
            }
          }
        } else if (!demoMode) {
          setChapter(staticPodcasts);
          for (const item of staticPodcasts) {
            if (item?.src) {
              addTrack(item.src);
            }
          }
        }
      })
      .catch(() => {
        if (!demoMode) {
          setChapter(staticPodcasts);
          for (const item of staticPodcasts) {
            if (item?.src) {
              addTrack(item.src);
            }
          }
        }
      });
  }, [addTrack, loadPodast, demoMode]);

  useEffect(() => {
    if (userState?._id) {
      trackEvent(EActivityEvent.PodcastPageViewed, {
        app: 'web',
      });
    }
  }, [userState?._id]);

  useEffect(() => {
    const startTime = startTimeRef.current || new Date();
    return () => {
      if (chapter[currentTrackIndex]?.name) {
        const duraction = differenceInSeconds(
          new Date(),
          startTime || new Date()
        );
        // modify startTimeRef to ensure it is reset for the next play
        startTimeRef.current = Date.now();
        trackEvent('Podcast Played', {
          chapter: chapter[currentTrackIndex].name,
          id: chapter[currentTrackIndex].id,
          app: 'web',
          duration: Math.abs(duraction),
          podcastDuration: chapter[currentTrackIndex].duration,
        });
      }
    };
  }, [chapter, currentTrackIndex]);

  const handlePlay = async () => {
    play();
    try {
      if (userState?._id) {
        //trackEvent(EActivityEvent.PodcastPlayed, {
        //  chapter: chapter[currentTrackIndex].name,
        //  id: chapter[currentTrackIndex].id,
        //  app: 'web',
        //  podcastDuration: chapter[currentTrackIndex].duration,
        //});
        await recordActivity(config.apiUrl, {
          data: {
            user: userState?._id,
            event: EActivityEvent.PodcastPlayed,
            source: 'web',
            data: {
              chapter: chapter[currentTrackIndex].name,
              id: chapter[currentTrackIndex].id,
              app: 'web',
            },
          },
        });
      }
    } catch {
      // do nothing
    }
  };

  if (loading) {
    return <Text.Body>Loading...</Text.Body>;
  }

  return (
    <PageLayout title="Podcast | Multiplayer">
      {/* <CallInterface title="Daily briefing" participants={participants} /> */}
      <div
        className={css`
          width: 100%;
          max-width: 688px;
          margin: auto;
          padding: 0px;
        `}
      >
        {/* <MultiplayerLogoSimple style={{ marginBottom: "24px" }} /> */}
        <div>
          <PodcastHeader lastUpdated={lastUpdated} />
        </div>
        <div
          className={css`
            width: fit-content;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            margin-top: 24px;
            margin-bottom: 32px;
          `}
        >
          <TabItems
            title="CHAPTERS"
            onClick={() => {
              setTab('CHAPTERS');
            }}
            active={tab === 'CHAPTERS'}
          />
          <TabItems
            title="TRANSCRIPT"
            onClick={() => {
              setTab('TRANSCRIPT');
            }}
            active={tab === 'TRANSCRIPT'}
          />
        </div>
        <div
          className={css`
            width: 100%;
            max-height: 253px;
            margin-bottom: 24px;
            overflow-x: auto;
            scrollbar-color: #29282f #1a191e;
            scrollbar-width: thin;
            &::-webkit-scrollbar {
              width: 8px;
            }
            &::-webkit-scrollbar-track {
              background: #1a191e;
            }
            @media (max-height: 700px) {
              max-height: 223px;
            }
          `}
        >
          {chapter?.length === 0 && (
            <Text.Body>
              No podcast available yet. You will receive your podcast everyday
              at 6:00 AM from Monday to Friday.
            </Text.Body>
          )}
          {tab === 'CHAPTERS' ? (
            chapter.map((item, index) => (
              <ChapterItem
                key={item.name}
                title={item.name}
                playing={currentTrackIndex === index && isPlaying}
                onClick={() => {
                  const trackIndex = currentTrackIndex;
                  seekToTrackIndex(index);
                  setTimeout(() => {
                    if (isPlaying && trackIndex === index) {
                      pause();
                    } else {
                      handlePlay();
                    }
                  }, 300);
                }}
                duration={item.duration}
              />
            ))
          ) : (
            <div
              className={css``}
              dangerouslySetInnerHTML={{
                __html:
                  chapter[currentTrackIndex]?.transcript ||
                  'You can read the transcript of the chapter being played here',
              }}
            />
          )}
        </div>
        <AudioPlayerView
          isPlaying={isPlaying}
          play={handlePlay}
          pause={pause}
          seekToTime={seekToTime}
          currentTime={currentTime}
          duration={chapter[currentTrackIndex]?.duration}
        />
      </div>
    </PageLayout>
  );
};
